import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MidNavBar from '../common/MidNavBar';
import ProductCard from '../common/ProductCard';
import { HOMEPAGE_COLLECTIONS } from '../../shared/homepageCollections';
import './HomePage.css';

// Per-collection hero presentation (gradient + tagline), keyed by the shared
// collection value. The list of collections (and their labels) comes from the
// shared source of truth so the hero slides never drift from the backend.
const HERO_PRESENTATION = {
  top_picks: { bg: 'linear-gradient(135deg, #800020 0%, #7B1FA2 100%)', text: 'Curated with Love' },
  new_arrivals: { bg: 'linear-gradient(135deg, #6A1B9A 0%, #C2185B 100%)', text: 'Fresh From the Studio' },
  high_discounts: { bg: 'linear-gradient(135deg, #722F37 0%, #800020 100%)', text: 'Best Deals Today' },
  seasonal: { bg: 'linear-gradient(135deg, #800000 0%, #6A1B9A 100%)', text: 'Season\'s Favourites' },
  buy_again: { bg: 'linear-gradient(135deg, #C2185B 0%, #7B1FA2 100%)', text: 'Your Past Loves' },
  gifts: { bg: 'linear-gradient(135deg, #7B1FA2 0%, #800020 100%)', text: 'Perfect for Gifting' },
};
const DEFAULT_PRESENTATION = { bg: 'linear-gradient(135deg, #800020 0%, #7B1FA2 100%)', text: '' };

const HERO_SLIDES = HOMEPAGE_COLLECTIONS.map((collection) => ({
  label: collection.label,
  filter: collection.value,
  ...(HERO_PRESENTATION[collection.value] || DEFAULT_PRESENTATION),
}));

const INFO_ITEMS = [
  { key: 'shipping', icon: '🚚', title: 'Shipping & Delivery', desc: 'Fast & secure shipping pan-India' },
  { key: 'helpdesk', icon: '🎧', title: 'Help Desk', desc: '24×7 customer support' },
  { key: 'eco', icon: '🌿', title: 'Eco-Friendly', desc: 'Sustainable packaging always' },
  { key: 'india', icon: '🇮🇳', title: 'Made in India', desc: 'Crafted by Indian artisans' },
  { key: 'aesthetic', icon: '✨', title: 'Aesthetic Design', desc: 'Timeless, curated collections' },
  { key: 'resolution', icon: '⚡', title: 'Fast Resolution', desc: 'Issues resolved within 24 hrs' },
];

const HAPPY_CUSTOMERS = [];

export default function HomePage() {
  const navigate = useNavigate();
  const [heroSlide, setHeroSlide] = useState(0);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [custSlide, setCustSlide] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [policiesOpen, setPoliciesOpen] = useState(false);

  useEffect(() => {
    axios.get('/api/products?sort=rating&limit=8').then(res => {
      setProducts(res.data.products || []);
    }).catch(() => setProducts([])).finally(() => setLoadingProducts(false));

    // Fetch real reviews from the database
    axios.get('/api/reviews?limit=6').then(res => {
      setReviews(res.data.reviews || []);
    }).catch(() => setReviews([]));

    const t = setInterval(() => setHeroSlide(s => (s + 1) % HERO_SLIDES.length), 4000);
    return () => { clearInterval(t); };
  }, []);

  // Auto-rotate reviews
  useEffect(() => {
    if (reviews.length === 0) return;
    const c = setInterval(() => setCustSlide(s => (s + 1) % reviews.length), 3500);
    return () => clearInterval(c);
  }, [reviews]);

  const slide = HERO_SLIDES[heroSlide];

  return (
    <div className="page-container">
      {/* Hero Slides */}
      <div className="hero-section" style={{ background: slide.bg }}>
        <div className="hero-slide-content">
          <p className="hero-sub">Abhiras Jewellery Presents</p>
          <h1 className="hero-title">{slide.label}</h1>
          <p className="hero-tagline">{slide.text}</p>
          <button className="hero-cta" onClick={() => navigate(`/catalog?filter=${slide.filter}`)}>
            Explore Collection →
          </button>
        </div>
        <div className="hero-dots">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} className={`dot${i === heroSlide ? ' active' : ''}`} onClick={() => setHeroSlide(i)} />
          ))}
        </div>
        <div className="hero-slide-nav">
          {HERO_SLIDES.map((s, i) => (
            <button key={s.label} className={`slide-tab${i === heroSlide ? ' active' : ''}`}
              onClick={() => { setHeroSlide(i); navigate(`/catalog?filter=${s.filter}`); }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <MidNavBar />

      {/* Catalog Sample */}
      <div className="section-heading">Our <span>Finest</span> Picks</div>
      <div className="catalog-grid">
        {loadingProducts
          ? Array(8).fill(0).map((_, i) => <div key={i} className="product-skeleton" />)
          : products.slice(0, 8).map(p => <ProductCard key={p.product_id} product={p} />)
        }
      </div>
      <div className="see-more-wrap">
        <button className="btn-primary see-more-btn" onClick={() => navigate('/catalog')}>
          See More Products
        </button>
      </div>

      {/* Happy Customers */}
      {reviews.length > 0 && <>
        <div className="heading-bar">Happy Customers ✦</div>
        <div className="customers-section">
          <div className="customers-track">
            {reviews.map((r, i) => (
              <div key={r.id || i} className={`customer-card${i === custSlide ? ' active' : ''}`}>
                <div className="customer-avatar">{(r.author_name || r.username || 'C').charAt(0).toUpperCase()}</div>
                <div>
                  <p className="customer-name">{r.author_name || r.username || 'Customer'}</p>
                  <p className="customer-comment">"{r.description && r.description.length > 110 ? r.description.slice(0, 110) + '…' : r.description}"</p>
                </div>
              </div>
            ))}
          </div>
          <div className="cust-dots">
            {reviews.map((_, i) => (
              <button key={i} className={`dot${i === custSlide ? ' active' : ''}`} onClick={() => setCustSlide(i)} />
            ))}
          </div>
        </div>
      </>}

      {/* Important Info */}
      <div className="heading-bar">Why Abhiras ✦</div>
      <div className="info-grid">
        {INFO_ITEMS.map(item => (
          <button key={item.key} className="info-square"
            onClick={() => navigate(`/information?section=${item.key}`)}>
            <span className="info-icon">{item.icon}</span>
            <span className="info-title">{item.title}</span>
            <span className="info-desc">{item.desc}</span>
          </button>
        ))}
      </div>

      {/* Track Order */}
      <button className="track-btn" onClick={() => navigate('/cart?tab=current')}>
        <span>Track Your Order</span>
        <span className="track-arrow">→</span>
      </button>

      {/* Accordion sections */}
      <div className="accordion-section">
        <AccordionItem title="Contact Us" open={contactOpen} toggle={() => setContactOpen(o => !o)}>
          <div className="contact-info">
            <a href="https://instagram.com/abhirasjewellery" target="_blank" rel="noreferrer">📸 @abhirasjewellery</a>
            <a href="tel:+919912369733">📞 +91 99123 69733</a>
            <a href="tel:+916380411386">📞 +91 63804 11386</a>
            <a href="mailto:abhirasjewellerycustomerquery@gmail.com">✉ abhirasjewellerycustomerquery@gmail.com</a>
            <span className="contact-address">📍 Vanasthalipuram, Hyderabad, India - 500070</span>
          </div>
        </AccordionItem>

        <AccordionItem title="About Us" open={aboutOpen} toggle={() => setAboutOpen(o => !o)}>
          <p className="accordion-text">
            Abhiras Jewellery was born from a passion for timeless beauty and Indian craftsmanship. 
            Every piece in our collection is thoughtfully designed, combining traditional artistry with contemporary elegance. 
            We celebrate the woman, the man, and the child who wears our jewellery — each one unique, each one radiant.
          </p>
        </AccordionItem>

        <AccordionItem title="Policies" open={policiesOpen} toggle={() => setPoliciesOpen(o => !o)}>
          <p className="accordion-text">
            <strong>Shipping:</strong> Standard delivery 3–7 business days.<br/>
            <strong>Privacy:</strong> Your data is never shared with third parties. We value your trust above all.<br/>
            <strong>All sales are final.</strong> We do not offer returns or exchanges.
          </p>
        </AccordionItem>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-logo">Abhiras Jewellery</div>
        <p>© {new Date().getFullYear()} Abhiras Jewellery. All rights reserved.</p>
        <p>Crafted with ♥ in India</p>
      </footer>
    </div>
  );
}

function AccordionItem({ title, open, toggle, children }) {
  return (
    <div className="accordion-item">
      <button className="accordion-header" onClick={toggle}>
        <span>{title}</span>
        <span className="acc-plus">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
}
